import React, { createContext, useContext, useEffect, useState } from "react";
import { saveDraftNow } from "@/lib/draft";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Reset: alles wissen wat we niet meer nodig hebben (taal-, thema- en weergavevoorkeuren blijven)
export function resetStorage() {
  const keep = ["cb_lang", "cb_lang_manual", "cb_theme", "campad.view", "campad.map.zoom", "campad.map.pan", "campad.map.3d"]
    .map((k) => [k, localStorage.getItem(k)]);
  localStorage.clear();
  sessionStorage.clear();
  keep.forEach(([k, v]) => { if (v) localStorage.setItem(k, v); });
}

// De sessie zelf zit in een httpOnly cookie (niet leesbaar voor JavaScript).
// Dit is enkel een vlaggetje zodat de app weet of ze een sessie mag verwachten.
const markSession = () => localStorage.setItem("cb_session", "1");
export const hasSession = () => !!localStorage.getItem("cb_session");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in

  // Na elke (her)start: sessie wissen → altijd eerst intro + aanmeldscherm.
  // Het printvenster opent in een nieuw tabblad en moet de sessie wél houden.
  useEffect(() => {
    let active = true;
    async function start() {
      const isPrint = window.location.pathname.startsWith("/print");
      if (!isPrint) {
        resetStorage();
        try { await api.post("/auth/session-clear"); } catch (e) { console.warn("Sessie wissen mislukt", e?.message); }
        if (active) setUser(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        markSession();
        if (active) setUser(data);
      } catch (e) {
        localStorage.removeItem("cb_session");
        if (active) setUser(false);
      }
    }
    start();
    return () => { active = false; };
  }, []);

  const login = async (email, password, extra = {}) => {
    const { data } = await api.post("/auth/login", { email, password, ...extra }, { timeout: 25000 });
    if (data.twofa_required) return data; // sms-code vereist, nog geen sessie
    markSession();
    setUser(data.user);
    return data.user;
  };

  const verifyTwofa = async (challengeId, code) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_id: challengeId, code });
    markSession();
    setUser(data.user);
    return data.user;
  };

  const resendTwofa = async (challengeId) => (await api.post("/auth/2fa/resend", { challenge_id: challengeId })).data;

  // Demo: de server meldt de demo-gebruiker aan, zodat het wachtwoord nooit in de browsercode staat
  const demoLogin = async () => {
    const { data } = await api.post("/demo/login");
    markSession();
    setUser(data.user);
    return data.user;
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  // Stille hartslag: houdt het laatste activiteitsmoment bij voor de urenregistratie
  useEffect(() => {
    if (!user || user === false) return;
    const beat = () => api.post("/timesheets/heartbeat").catch((e) => console.debug("Hartslag niet verstuurd", e?.message));
    const id = setInterval(beat, 2 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  // reason: "logout" (naar huis), "break" (pauze) of "idle" (inactiviteit)
  const logout = async (reason = "logout", note = "") => {
    if (reason === "idle") await saveDraftNow("idle"); // half ingevuld werk bewaren vóór de sessie weg is
    try {
      await api.post("/timesheets/clock-out", { reason, note });
    } catch (e) {
      console.warn("Uren bij afmelden niet geregistreerd", e?.message); // mag afmelden niet blokkeren
    }
    const exitUrl = reason === "logout" ? localStorage.getItem("cb_logout_url") : null;
    try { await api.post("/auth/session-clear"); } catch (e) { console.warn("Sessiecookie wissen mislukt", e?.message); }
    resetStorage();
    setUser(false);
    // demo-omgevingen sturen de bezoeker terug naar de bedrijfswebsite
    window.location.href = exitUrl || "/login"; // volledige herlaad = reset van alle state, daarna intro + aanmelden
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, verifyTwofa, resendTwofa, demoLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
